/**
 * FileMakerObjectClipperPlugin.ts - Multi-package plugin for FileMaker object clipboard operations
 * 
 * CHANGE HISTORY:
 * - 251129_1622 [pending] danielbeer: Initial creation - orchestrates Clippr + Translatr for FM objects
 * 
 * ARCHITECTURE:
 * This is a multi-package plugin (lives in Pluggr per ARCHITECTURE.md)
 * 
 * DEPENDENCIES:
 * - @opcentral/clippr (clipboard operations, FM XML handling)
 * - @opcentral/translatr (XML conversion, dependency analysis)
 * 
 * LOCATION: packages/pluggr/src/plugins/filemaker/
 * REASON: Orchestrates multiple packages (Clippr + Translatr)
 */

import type { Plugin, PluginManifest } from '../../PluggrTypes';
import { getFileMakerClipboard } from '@opcentral/clippr';
import type { FileMakerClipboardItem } from '@opcentral/clippr';
import { 
  FileMakerScriptToXML,
  FileMakerDependencyAnalyzer
} from '@opcentral/translatr';
import type { 
  CustomFunction,
  DeploymentPlan
} from '@opcentral/translatr';

/**
 * Format options for copying FileMaker objects
 */
export type CopyFormat = 'xml' | 'readable' | 'printable' | 'json';

/**
 * Options for copying FileMaker objects
 */
export interface CopyOptions {
  format?: CopyFormat;
  includeMetadata?: boolean;
  analyzeDependencies?: boolean;
}

/**
 * Result of paste operation
 */
export interface PasteResult {
  success: boolean;
  item?: FileMakerClipboardItem;
  format?: string;
  error?: string;
}

/**
 * Result of dependency analysis
 */
export interface DependencyAnalysisResult {
  success: boolean;
  plan?: DeploymentPlan;
  missing?: string[];
  circular?: string[][];
  validation?: {
    valid: boolean;
    errors: string[];
    warnings: string[];
  };
  error?: string;
}

/**
 * FileMaker Object Clipper
 * Orchestrates Clippr and Translatr for comprehensive FM object clipboard operations
 */
export class FileMakerObjectClipper {
  private fmClipboard = getFileMakerClipboard();
  private scriptConverter = new FileMakerScriptToXML();
  private dependencyAnalyzer = new FileMakerDependencyAnalyzer();
  
  /**
   * Copy FileMaker objects to clipboard in specified format
   */
  async copyObjects(objects: any[], options: CopyOptions = {}): Promise<boolean> {
    const format = options.format || 'xml';
    
    try {
      switch (format) {
        case 'xml':
          return await this.copyAsXML(objects);
        
        case 'readable':
          return await this.copyAsReadable(objects);
        
        case 'printable':
          return await this.copyAsPrintable(objects);
        
        case 'json':
          return await this.copyAsJSON(objects);
        
        default:
          throw new Error(`Unknown format: ${format}`);
      }
    } catch (error) {
      console.error('Failed to copy objects:', error);
      return false;
    }
  }
  
  /**
   * Copy as FileMaker XML (pasteable into FileMaker)
   */
  private async copyAsXML(objects: any[]): Promise<boolean> {
    // If objects are already XML strings, use them directly
    if (objects.length === 1 && typeof objects[0] === 'string' && objects[0].includes('fmxmlsnippet')) {
      return await this.fmClipboard.writeFileMakerObject(objects[0]);
    }
    
    // If objects are script definitions, convert to XML
    if (objects[0]?.steps) {
      const xml = this.scriptConverter.convertToXML(objects[0]);
      return await this.fmClipboard.writeFileMakerObject(xml);
    }
    
    // For custom functions, generate XML
    if (objects[0]?.calculation) {
      const xml = this.generateCustomFunctionsXML(objects);
      return await this.fmClipboard.writeFileMakerObject(xml);
    }
    
    return false;
  }
  
  /**
   * Copy as readable script steps (for documentation)
   */
  private async copyAsReadable(objects: any[]): Promise<boolean> {
    try {
      let readable = '';
      
      if (objects[0]?.steps) {
        // Script object
        readable = this.formatScriptAsReadable(objects[0]);
      } else if (objects[0]?.calculation) {
        // Custom function
        readable = this.formatCustomFunctionsAsReadable(objects);
      } else if (typeof objects[0] === 'string' && objects[0].includes('fmxmlsnippet')) {
        // Parse XML and format
        const item = this.fmClipboard.parseFileMakerXML(objects[0]);
        readable = this.formatXMLAsReadable(item);
      }
      
      if (readable) {
        await navigator.clipboard.writeText(readable);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Failed to copy as readable:', error);
      return false;
    }
  }
  
  /**
   * Copy as printable format (formatted for printing/documentation)
   */
  private async copyAsPrintable(objects: any[]): Promise<boolean> {
    try {
      let printable = '';
      
      if (objects[0]?.steps) {
        printable = this.formatScriptAsPrintable(objects[0]);
      } else if (objects[0]?.calculation) {
        printable = this.formatCustomFunctionsAsPrintable(objects);
      }
      
      if (printable) {
        await navigator.clipboard.writeText(printable);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Failed to copy as printable:', error);
      return false;
    }
  }
  
  /**
   * Copy as JSON (for data exchange)
   */
  private async copyAsJSON(objects: any[]): Promise<boolean> {
    try {
      const json = JSON.stringify(objects, null, 2);
      await navigator.clipboard.writeText(json);
      return true;
    } catch (error) {
      console.error('Failed to copy as JSON:', error);
      return false;
    }
  }
  
  /**
   * Paste FileMaker object from clipboard
   */
  async pasteObject(): Promise<PasteResult> {
    try {
      const item = await this.fmClipboard.readFileMakerObject();
      
      if (!item) {
        return {
          success: false,
          error: 'No FileMaker object found in clipboard'
        };
      }
      
      return {
        success: true,
        item,
        format: item.type
      };
    } catch (error) {
      return {
        success: false,
        error: (error as Error).message
      };
    }
  }
  
  /**
   * Analyze dependencies in custom functions
   */
  analyzeDependencies(functions: CustomFunction[]): DependencyAnalysisResult {
    try {
      const plan = this.dependencyAnalyzer.createDeploymentPlan(functions);
      const missing = this.dependencyAnalyzer.getMissingDependencies(functions);
      const validation = this.dependencyAnalyzer.validateDeploymentPlan(plan);
      
      return {
        success: true,
        plan,
        missing,
        circular: plan.circular,
        validation
      };
    } catch (error) {
      return {
        success: false,
        error: (error as Error).message
      };
    }
  }
  
  /**
   * Check if clipboard has FileMaker object
   */
  async hasFileMakerObject(): Promise<boolean> {
    return await this.fmClipboard.hasFileMakerObject();
  }
  
  /**
   * Generate FileMaker XML for custom functions
   */
  private generateCustomFunctionsXML(functions: any[]): string {
    const xmlParts = ['<?xml version="1.0" encoding="UTF-8"?>'];
    xmlParts.push('<fmxmlsnippet type="FMObjectList">');
    
    for (const func of functions) {
      xmlParts.push('  <CustomFunction id="' + (func.id || '1') + '" functionArity="' + (func.parameters?.length || 0) + '" visible="True" parameters="' + (func.parameters?.join('; ') || '') + '" name="' + this.escapeXML(func.name) + '">');
      xmlParts.push('    <Calculation><![CDATA[' + (func.calculation || '') + ']]></Calculation>');
      if (func.description) {
        xmlParts.push('    <Description>' + this.escapeXML(func.description) + '</Description>');
      }
      xmlParts.push('  </CustomFunction>');
    }
    
    xmlParts.push('</fmxmlsnippet>');
    return xmlParts.join('\n');
  }
  
  /**
   * Format script as readable text
   */
  private formatScriptAsReadable(script: any): string {
    const lines: string[] = [];
    lines.push(`Script: ${script.name}`);
    lines.push('');
    
    for (const step of script.steps) {
      const indent = '  '.repeat(this.getStepIndentLevel(step));
      let line = indent + step.name;
      
      if (step.calculation) {
        line += ` [ ${step.calculation} ]`;
      }
      if (step.text) {
        line += ` // ${step.text}`;
      }
      
      lines.push(line);
    }
    
    return lines.join('\n');
  }
  
  /**
   * Format custom functions as readable text
   */
  private formatCustomFunctionsAsReadable(functions: any[]): string {
    const lines: string[] = [];
    
    for (const func of functions) {
      lines.push(`Function: ${func.name}`);
      if (func.parameters && func.parameters.length > 0) {
        lines.push(`Parameters: ${func.parameters.join(', ')}`);
      }
      if (func.description) {
        lines.push(`Description: ${func.description}`);
      }
      lines.push('');
      lines.push('Calculation:');
      lines.push(func.calculation);
      lines.push('');
      lines.push('---');
      lines.push('');
    }
    
    return lines.join('\n');
  }
  
  /**
   * Format script as printable (with formatting)
   */
  private formatScriptAsPrintable(script: any): string {
    const lines: string[] = [];
    lines.push('═'.repeat(80));
    lines.push(`SCRIPT: ${script.name}`);
    lines.push('═'.repeat(80));
    lines.push('');
    
    let stepNum = 1;
    for (const step of script.steps) {
      const indent = '  '.repeat(this.getStepIndentLevel(step));
      lines.push(`${String(stepNum).padStart(3, ' ')}. ${indent}${step.name}`);
      
      if (step.calculation) {
        lines.push(`     ${indent}  → ${step.calculation}`);
      }
      if (step.text) {
        lines.push(`     ${indent}  // ${step.text}`);
      }
      
      stepNum++;
    }
    
    lines.push('');
    lines.push('═'.repeat(80));
    
    return lines.join('\n');
  }
  
  /**
   * Format custom functions as printable
   */
  private formatCustomFunctionsAsPrintable(functions: any[]): string {
    const lines: string[] = [];
    lines.push('═'.repeat(80));
    lines.push('CUSTOM FUNCTIONS');
    lines.push('═'.repeat(80));
    lines.push('');
    
    for (let i = 0; i < functions.length; i++) {
      const func = functions[i];
      
      lines.push(`${i + 1}. ${func.name}`);
      lines.push('─'.repeat(80));
      
      if (func.parameters && func.parameters.length > 0) {
        lines.push(`   Parameters: ${func.parameters.join(', ')}`);
      }
      
      if (func.description) {
        lines.push(`   Description: ${func.description}`);
      }
      
      lines.push('');
      lines.push('   Calculation:');
      const calcLines = func.calculation.split('\n');
      for (const calcLine of calcLines) {
        lines.push(`   ${calcLine}`);
      }
      
      lines.push('');
    }
    
    lines.push('═'.repeat(80));
    
    return lines.join('\n');
  }
  
  /**
   * Format XML as readable text
   */
  private formatXMLAsReadable(item: FileMakerClipboardItem): string {
    const lines: string[] = [];
    lines.push(`FileMaker ${item.type}: ${item.name || 'Unnamed'}`);
    lines.push('');
    
    if (item.metadata) {
      lines.push('Metadata:');
      for (const [key, value] of Object.entries(item.metadata)) {
        lines.push(`  ${key}: ${value}`);
      }
      lines.push('');
    }
    
    lines.push('XML:');
    lines.push(item.xml);
    
    return lines.join('\n');
  }
  
  /**
   * Get indent level for script step (for control structures)
   */
  private getStepIndentLevel(step: any): number {
    const name = step.name.toLowerCase();
    
    if (name.includes('end if') || name.includes('end loop') || name === 'else') {
      return 0;
    }
    
    if (name.includes('if') || name.includes('loop')) {
      return 0;
    }
    
    // Steps inside control structures would be indented
    // This is simplified - real implementation would track nesting
    return 0;
  }
  
  /**
   * Escape XML special characters
   */
  private escapeXML(text: string): string {
    if (!text) return '';
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  }
}

/**
 * Plugin manifest
 */
export const manifest: PluginManifest = {
  id: 'filemaker-object-clipper',
  name: 'FileMaker Object Clipper',
  version: '1.0.0',
  description: 'Copy/paste FileMaker objects with format conversion and dependency analysis',
  author: 'OpCentral',
  main: './plugins/filemaker/FileMakerObjectClipperPlugin',
  category: 'filemaker-scripts',
  tags: ['filemaker', 'clipboard', 'xml', 'scripts', 'custom-functions', 'conversion'],
  permissions: {
    files: 'none',
    network: 'none'
  },
  features: [
    'copy-as-xml',
    'copy-as-readable',
    'copy-as-printable',
    'paste-from-clipboard',
    'dependency-analysis',
    'format-conversion'
  ]
};

/**
 * FileMaker Object Clipper Plugin
 */
export const fileMakerObjectClipperPlugin: Plugin = {
  manifest,
  
  async onload(context) {
    console.log('[FileMakerObjectClipper] Plugin loaded');
    
    const clipper = new FileMakerObjectClipper();
    
    // Register functions for FileMaker WebViewer
    context.registerFunction?.('fm_copyObjectAsXML', async (param: string) => {
      try {
        const objects = JSON.parse(param);
        const success = await clipper.copyObjects(objects, { format: 'xml' });
        return { success, format: 'xml' };
      } catch (error) {
        return { success: false, error: (error as Error).message };
      }
    });
    
    context.registerFunction?.('fm_copyObjectAsReadable', async (param: string) => {
      try {
        const objects = JSON.parse(param);
        const success = await clipper.copyObjects(objects, { format: 'readable' });
        return { success, format: 'readable' };
      } catch (error) {
        return { success: false, error: (error as Error).message };
      }
    });
    
    context.registerFunction?.('fm_copyObjectAsPrintable', async (param: string) => {
      try {
        const objects = JSON.parse(param);
        const success = await clipper.copyObjects(objects, { format: 'printable' });
        return { success, format: 'printable' };
      } catch (error) {
        return { success: false, error: (error as Error).message };
      }
    });
    
    context.registerFunction?.('fm_pasteObject', async () => {
      return await clipper.pasteObject();
    });
    
    context.registerFunction?.('fm_analyzeDependencies', async (param: string) => {
      try {
        const functions = JSON.parse(param);
        return clipper.analyzeDependencies(functions);
      } catch (error) {
        return { success: false, error: (error as Error).message };
      }
    });
    
    context.registerFunction?.('fm_hasFileMakerObject', async () => {
      return await clipper.hasFileMakerObject();
    });
  },
  
  async onunload() {
    console.log('[FileMakerObjectClipper] Plugin unloaded');
  }
};

export default fileMakerObjectClipperPlugin;
